import { createTheme } from '@mui/material/styles';

export const STYLE = {
  FONT: {
    PRIMARY_FONT: 'Poppins, sans-serif, Arial',
    SIZE: {
      HEADING_BIG: 36,
      HEADING_MEDIUM: 30,
      HEADING: 24,
      HEADING_1: 18,
      HEADING_2: 20,
      PARAGRAPH_BIG: 17,
      PARAGRAPH: 15,
      PARAGRAPH_SMALL: 13,
    },
  },
  COLOR: {
    ACCENT_PRIMARY: '#00346B',
    ACCENT_PRIMARY_DARKER: '#00294F',
    ACCENT_PRIMARY_LIGHTER: '#004A91',
    ACCENT_PRIMARY_LIGHTER2: '#66A9D9',
    ACCENT_PRIMARY_LIGHTER3: '#D4E7F5',
    ACCENT_PRIMARY_DISABLED: 'rgba(0, 52, 107, 0.1)',
    LIGHT_BLUE: '#EFF6FF',
    SUCCESS: '#1BB161',
    SUCCESS_LIGHTER: 'rgba(27, 177, 97, 0.1)',
    ERROR: '#DF252A',
    ERROR_LIGHTER: 'rgba(223, 37, 42, 0.1)',
    ERROR_ICON: '#DF252A',
    ERROR_ICON_LIGHTER: 'rgba(223, 37, 42, 0.1)',
    WARNING: '#F59A00',
    WARNING_LIGHTER: 'rgba(245, 154, 0, 0.1)',
    TEXT1: '#2B2A29',
    TEXT2: '#727271',
    TEXT3: '#B3B3B2',
    TEXT4: '#262626',
    ICONS1: '#2B2A29',
    BORDER1: '#2B2A2933',
    BORDER_DASHED: '#D9D9D9',
    ICONS2: '#727271',
    ICONS3: '#B3B3B2',
    INPUT_BORDER: '#66A9D9',
    DIVIDER: 'rgba(43, 42, 41, 0.1)',
    INPUT_BACKGROUND: 'rgba(228, 231, 233, 0.4)',
    BOX_SHADOW: 'rgba(228, 231, 233, 0.4)',
    TABLE_CONTENT: 'rgba(231, 231, 231, 0.2)',
    BLACK: '#000000',
    WHITE: '#FFF',
    BUTTON_OUTLINED_DISABLED: '#083F8D',
    STROKE_DEFAULT: 'rgba(43, 42, 41, 0.2)',
    TABS_BORDER: '#EAEAEA',
    BACKGROUND_LIGHT: '#F0F4F9',
    CARD_BORDER: '#DEE7F4',
    MODAL_BACKDROP: 'rgba(0, 0, 0, 0.3)',
    WARNING_NOTE: '#FFF4BA',
    CAPTURE_BUTTONS_BOX_SHADOW:
      '-0.6px 1.5px 48px -12px rgba(0, 0, 0, 0.15), -0.1px 0.25px 12px -8px rgba(0, 0, 0, 0.15), 0.119px -0.298px 9.52px 0 rgba(255, 255, 255, 0.15) inset, 0.069px -0.172px 4.76px 0 rgba(255, 255, 255, 0.15) inset',
    CAPTURE_BUTTONS_BACKGROUND_COLOR: 'rgba(255, 255, 255, 0.1)',
    SUCCESS_BACKGROUND: '#DDF3E7',
    HEADER_MENU_BOX_SHADOW: '0 16px 20px -8px rgba(228, 231, 233, 0.40)',
    PENDING: '#FEF4E2',
    WARNING_LIGHT: '#FEF3B3',
    GRAY: '#E0E0E0',
    DISABLED_BACKGROUND: '#E4E7E966',
    BACKGROUND_OVERLAY: 'rgba(0, 0, 0, 0.35)',
    BLUE: '#BEDBFF',
  },
};

/* Custom theme defined for the whole App
   Ref to https://material-ui.com/customization/theming/#theme-configuration-variables
   for API exposure of consumables
*/
export const theme = createTheme({
  typography: {
    fontFamily: [STYLE.FONT.PRIMARY_FONT].join(','),
    h1: {
      fontSize: 32,
      fontWeight: '600',
      lineHeight: '140%',
    },
    h2: {
      fontSize: 24,
      fontWeight: '600',
      lineHeight: '140%',
    },
    h3: {
      fontSize: 20,
      fontWeight: '600',
      lineHeight: '140%',
    },
    h4: {
      fontSize: 18,
      fontWeight: '600',
      lineHeight: '140%',
    },
    h5: {
      fontSize: 16,
      fontWeight: '600',
      lineHeight: '24px',
    },
    h6: {
      fontSize: 14,
      fontWeight: '600',
      lineHeight: '20px',
    },
    body1: {
      fontSize: 16,
      fontWeight: '400',
      lineHeight: '24px',
    },
    body2: {
      fontSize: 12,
      fontWeight: '400',
      lineHeight: '18px',
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: '400',
      lineHeight: '24px',
    },
    subtitle2: {
      fontSize: 12,
      fontWeight: '400',
      lineHeight: '18px',
    },
    button: {
      fontSize: 14,
      fontWeight: '500',
      lineHeight: '20px',
      textTransform: 'none',
    },
  },
  palette: {
    primary: {
      main: STYLE.COLOR.ACCENT_PRIMARY,
      light: STYLE.COLOR.ACCENT_PRIMARY_LIGHTER,
      dark: STYLE.COLOR.ACCENT_PRIMARY_DARKER,
      contrastText: STYLE.COLOR.WHITE,
    },
    success: {
      main: STYLE.COLOR.SUCCESS,
      light: STYLE.COLOR.SUCCESS_LIGHTER,
      contrastText: STYLE.COLOR.WHITE,
    },
    error: {
      main: STYLE.COLOR.ERROR,
      light: STYLE.COLOR.ERROR_LIGHTER,
      contrastText: STYLE.COLOR.WHITE,
    },
    warning: {
      main: STYLE.COLOR.WARNING,
      light: STYLE.COLOR.WARNING_LIGHTER,
      contrastText: STYLE.COLOR.WHITE,
    },
    info: {
      main: STYLE.COLOR.ACCENT_PRIMARY,
      light: STYLE.COLOR.ACCENT_PRIMARY_LIGHTER,
      dark: STYLE.COLOR.ACCENT_PRIMARY_DARKER,
      contrastText: STYLE.COLOR.WHITE,
    },
    divider: STYLE.COLOR.DIVIDER,
    background: {
      default: STYLE.COLOR.WHITE,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none !important',
          textTransform: 'none',
          height: '52px',
          borderRadius: '30px',
          outline: 'none !important',
          '@media (max-width: 600px)': {
            height: '44px',
          },
          '&.MuiButton-contained': {
            minWidth: 120,
          },
          '&.MuiButton-outlined': {
            minWidth: 120,
          },
          '&.MuiButton-text': {
            minWidth: 'initial',
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: STYLE.COLOR.STROKE_DEFAULT,
          borderRadius: '4px',
          '& .MuiIconButton-label': {
            color: STYLE.COLOR.TEXT1,
            fontWeight: '400',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: STYLE.COLOR.STROKE_DEFAULT,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: '48px',
          color: STYLE.COLOR.TEXT1,
          fontSize: `14px`,
          lineHeight: '20px',
          '& .MuiOutlinedInput-input': {
            padding: '0px !important',
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px !important',
            },
          },
          '@media (max-width: 600px)': {
            height: '44px',
            fontSize: `12px`,
            lineHeight: '18px',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          height: '48px',
          color: STYLE.COLOR.TEXT1,
          // "& .MuiOutlinedInput-input": {
          //   padding: "14px 20px 14px 20px !important",
          // },
          // "& .MuiAutocomplete-hasPopupIcon": {
          //   padding: "14px 20px 14px 20px !important",
          // },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          body: {
            '&::focus': {
              outline: 'none',
            },
            '&.using-mouse': {
              '& button': {
                outline: 'none',
              },
            },
          },
          html: {
            /* Styles for Google Maps Suggestion Box
               These styles are injected globally, due to how Google inserts it's component
            */
            overflowY: 'scroll',
          },
        },
      },
    },
  },
});
